import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';

import { Option } from "@shared/Entities/General/option.entity";
import { Perfil } from "@shared/Entities/Perfil/perfil.entity";
import { Rol } from "@shared/Entities/Rol/rol.entity";
import { ProfileName } from "@shared/Entities/Usuario/profile-name.enum";
import { PerfilRepository } from "@shared/Repositories/perfil.repository";
import { RolesRepository } from "@shared/Repositories/roles.repository";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { SharedOptionsService } from "@shared/Services/shared-options.service";
import { NewUserAccessData } from "src/app/Canales/Shared/Types/new-user";
import { AccessField } from "src/app/Canales/Shared/Types/Enums/access-field.enum";
import { UserCreationService } from "src/app/Canales/Shared/user-creation.service";
import { Usuario } from "@shared/Entities/Usuario/usuario.entity";
import { Router } from "@angular/router";
import { CanalesAccessFormService } from "../../../../Shared/Services/canales-access-form.service";


@Component({
  selector: "app-access-form-fiduciary",
  templateUrl: "./access-form-fiduciary.component.html",
})
export class AccessFormFiduciaryComponent implements OnInit {
  public form: UntypedFormGroup;
  public profiles$: Observable<Perfil[]>;
  public roles$: Observable<Rol[]>;
  public provinces$: Observable<Option[]>;
  public readonly ProfileName = ProfileName;
  public isUserCanales: boolean;
  public currentProfile: Perfil;
  public currentUser: Usuario;
  public isCanlesAdminFiduciary: boolean = false; 
  public isOperative: boolean;

  private readonly EDIT_USER_ROUTE = 'editar-usuario-fiduciaria';
  private readonly EDIT_USER_OPERATIONAL_ROUTE = 'editar-usuario-fiduciaria/operational';
  private readonly CREATE_USER_OPERATIONAL_ROUTE = 'create-fiduciary-user-canales/operational';

  private _isEditing: boolean = false;

  @Output() public submitForm = new EventEmitter<NewUserAccessData>();

  @Input() public set isEditing(editing: boolean) {
    this._isEditing = editing;
    if (editing) {
      this.form?.get("userCode").disable();
    } else {
      this.form?.get("userCode").enable();
    }
  }
  public get isEditing(): boolean {
    return this._isEditing;
  }

  @Input() public set accessData(newAccessData: NewUserAccessData | null) {
    newAccessData ? this.form.patchValue(newAccessData) : this.form?.reset();
    if (newAccessData?.roles) {
      const roles = newAccessData.roles.map((role) => {
        return {
          id: role.id,
          nombre: role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    }
  }

  public readonly AccessField = AccessField;
  public requiredFields: Partial<Record<AccessField, boolean>> = {};

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly profileService: PerfilRepository,
    private readonly rolesService: RolesRepository,
    private readonly userCreationService: UserCreationService,
    private readonly sharedOptionsService: SharedOptionsService,
    private readonly userRepository: UsuariosRepository,
    private readonly router: Router,
    private readonly canalesAccessFormService: CanalesAccessFormService 
  ) {}

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      company: ["", this.isRequired(AccessField.Company)],
      userCode: [""],
      clientCode: ["", this.isRequired(AccessField.ClientCode)],
      intermediaryCode: ["", this.isRequired(AccessField.IntermediaryCode)],
      identificationProfile: ["", this.isRequired(AccessField.ProfileIdentification)],
      officeCode: ["", this.isRequired(AccessField.OfficeCode)],
      profile: [null, Validators.required],
      roles: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      healthProviderCode: ["", this.isRequired(AccessField.HealthProviderCode)],
      isUserCanales: [false],
      isUserAdmin: [false],
      fiduciaryRnc: ["", this.isRequired(AccessField.FiduciaryRnc)],
    });
    
    this.isEditing = this.router.url.includes(this.EDIT_USER_ROUTE);

    this.isOperative = this.isEditing 
    ? this.router.url.includes(this.EDIT_USER_OPERATIONAL_ROUTE)
    : this.router.url.includes(this.CREATE_USER_OPERATIONAL_ROUTE);
    
    if (this.isEditing) {
      this.form.get("userCode").disable();
    }

    this.roles$ = this.rolesService.getRolesFiduciaria();
    this.profiles$ = this.profileService.getPerfilesFiduciaria();

    this.requiredFields = this.userCreationService.getRequiredFields();
    this.provinces$ = this.sharedOptionsService.getProvinces();

    this.form.get("profile").valueChanges.subscribe((profile: Perfil) => {
      this.requiredFields = this.userCreationService.getRequiredFields(profile?.nombre);
      this.refreshFormControls(this.form.controls);
      const roles = profile?.perfilRole?.map((role) => {
        return {
          id: role.roleId,
          nombre: role.role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    });

    this.mapFormData();
  }

  public mapRoles(): void {
    if (this.currentProfile && this.currentProfile?.id > 0) {
      const profileControl = this.form.get("profile");
      profileControl?.setValue(this.currentProfile);

      this.requiredFields = this.userCreationService.getRequiredFields(profileControl?.value?.nombre);
      const roles = profileControl?.value?.perfilRole?.map((role) => {
        return {
          id: role.roleId,
          nombre: role.role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    }
  }

  public mapFormData(): void {
    const excludedRole = this.isOperative ?     ['Administrador Corporativo Fiduciaria'] : ['Operativo Fiduciaria'] ;
    const excludedProfile = this.isOperative ?    ['Administrador Corporativo Fiduciaria'] : ['Operativo Fiduciaria'] ;
    excludedRole.push('Fiduciaria');
    excludedProfile.push('Fiduciaria');

    this.profiles$ = this.profiles$.pipe(
      map(profiles => {
        return profiles.filter(profile =>  !excludedRole.includes(profile.nombre));
      })
    );

    this.roles$ = this.roles$.pipe(
      map(roles => {
        return roles.filter(role => !excludedProfile.includes(role.nombre));
      })
    );

    this.userRepository.getInfoClienteActual().subscribe((usuario) => {
      this.isUserCanales = usuario.usuarioRole.some((x) => x.role.nombre === "Canales Fiduciaria");

      this.profiles$.subscribe((profiles) => {
        this.currentProfile = profiles.find((x) => x.nombre === usuario?.perfil.nombre);
      });
      this.mapRoles();

      const empresa = usuario?.metadata?.empresa;
      if (empresa) this.form.get("company")?.setValue(empresa);

      const codigoPrestador = usuario?.metadata?.codigoPrestador;
      const codigoIntermediario = usuario?.metadata?.codigoIntermediario;

      if (codigoPrestador) {
        const healthProviderCode = codigoPrestador.length === 5 ? codigoPrestador.slice(1) : codigoPrestador;
        this.form.get("healthProviderCode")?.setValue(healthProviderCode);
        this.form.get("identificationProfile").setValue(codigoPrestador);
      } else if (codigoIntermediario) {
        const intermediaryCode = codigoIntermediario.length === 5 ? codigoIntermediario.slice(1) : codigoIntermediario;
        this.form.get("intermediaryCode")?.setValue(intermediaryCode);
      }
    });
  }

  private refreshFormControls(controls: Record<string, AbstractControl>): void {
    this.canalesAccessFormService.refreshFormControls(controls);
  }

  private isRequired(field: AccessField[] | AccessField): ValidatorFn {
    return this.canalesAccessFormService.isRequired(field, this.requiredFields);
  }

  public onSubmit(): void {
    const submitValue: NewUserAccessData = this.canalesAccessFormService.onSubmit(this.form, this.requiredFields);
    this.submitForm.emit(submitValue);
  }

  public showField(field: AccessField): boolean {
    return this.canalesAccessFormService.showField(field, this.requiredFields);
  }

  public get profileName(): AbstractControl {
    return this.canalesAccessFormService.getFormValue(this.form, "profile");
  }

  public get isUserCanalesControl(): AbstractControl {
    return this.canalesAccessFormService.getFormValue(this.form, "isUserCanales");
  }

  public get isUserAdminControl(): AbstractControl {
    return this.canalesAccessFormService.getFormValue(this.form, "isUserAdmin");
  }
}
